import { BimIndexClient } from "./bimIndexClient";
import type { OpenResult, Requests } from "./protocol";

/**
 * Process-wide BIM-index session: one worker, one loaded index.
 *
 * An engine keeps decoded pages cached and holds a lock on every file it reads,
 * so every consumer — `window.bimIndex` and the data access layer — goes through
 * this rather than starting a second worker over the same index.
 */

let client: BimIndexClient | null = null;
let info: OpenResult | null = null;
let opening: Promise<OpenResult> | null = null;

/** The session's worker client, started on first use. */
export function bimIndexClient(): BimIndexClient {
    if (!client) {
        client = new BimIndexClient();
    }
    return client;
}

export function bimIndexInfo(): OpenResult | null {
    return info;
}

/** Load the index, replacing whatever is currently open. */
export async function openBimIndex(params: Requests["open"]["params"]): Promise<OpenResult> {
    opening = bimIndexClient().call("open", params);
    try {
        info = await opening;
        return info;
    } finally {
        opening = null;
    }
}

/**
 * Load the index unless it is already open (or opening) — the idempotent entry
 * point for callers that just need it available, such as the first query through
 * the data access layer.
 */
export function ensureBimIndexOpen(params: Requests["open"]["params"]): Promise<OpenResult> {
    if (info) {
        return Promise.resolve(info);
    }
    return opening ?? openBimIndex(params);
}

/**
 * Forget the index and terminate the worker — the only way to hand its linear
 * memory back to the browser.
 *
 * The session is free of it immediately; the worker gets asked to let go of its
 * OPFS files first, because terminating runs no code there and the files stay
 * locked until the browser tears the realm down. It is terminated either way, so
 * a worker that cannot answer is still dropped.
 */
export function closeBimIndex(): void {
    const closing = client;
    client = null;
    info = null;
    opening = null;
    if (!closing) {
        return;
    }
    const terminate = () => closing.terminate();
    void closing.call("close", undefined).then(terminate, terminate);
}
